import { useState } from "react";

import SvgBarChartSquare01 from "@uui/BarChartSquare01";
import SvgChevronRight from "@uui/ChevronRight";
import { ParentSize } from "@visx/responsive";
import { times } from "lodash/fp";
import { useNavigate } from "react-router-dom";

import {
  Box,
  Typography,
  Stack,
  Button,
  styled,
  Skeleton,
} from "@mui/material";

import { ReadAverages } from "@app/analytics/constants/readAverages";
import useRollupContext from "@app/analytics/hooks/meetops/useRollupContext";
import { ALL_TEAMS, ALL_USERS } from "@app/analytics/providers/RollupContext";
import ErrorContent from "@app/analytics/WorkspaceOverview/components/ErrorContent";
import ROUTES from "@app/common/routes";

import useMeetingManager, {
  MeetingManagerMetric,
  MeetingManagerResult,
} from "./useMeetingManager";
import EmptyContent from "../components/EmptyContent";
import HorizontalBarChart from "../components/HorizontalBarChart";
import ModuleHeader from "../components/ModuleHeader";
import ModuleTitle from "../components/ModuleTitle";
import { WorkspaceModule, WorkspaceModuleBody } from "../components/styles";

const StyledButton = styled(Button)(({ theme }) => ({
  padding: 2,
  flex: 1,
  height: "80px",
  border: "1px solid transparent",
  color: theme.palette.shades.primary[500],
  "&:hover": {
    backgroundColor: theme.palette.states.hover,
    borderColor: theme.palette.shades.primary[500],
  },
  "&.active": {
    backgroundColor: theme.palette.shades.primary[500],
    color: "white",
    "&:hover": {
      backgroundColor: theme.palette.shades.primary[400],
      borderColor: theme.palette.shades.primary[400],
    },
  },
}));

const Y_TICK_VALUES = [2, 5];

const LoadingState = () => {
  return (
    <Stack
      spacing={1}
      direction="column"
      alignItems="center"
      justifyContent="center"
      sx={{ width: "100%" }}
    >
      <Stack direction="row" spacing={1} sx={{ width: "100%" }}>
        {times(
          (i) => (
            <Skeleton
              key={i}
              variant="rectangular"
              sx={{
                height: "80px",
                borderRadius: "8px",
                flex: 1,
              }}
            />
          ),
          3
        )}
      </Stack>
      <Skeleton
        variant="rectangular"
        sx={{ width: "100%", height: "130px", borderRadius: "8px" }}
      />
    </Stack>
  );
};

const getChartValues = ({
  selectedMetric,
  workspaceAverages,
  readAverages,
}: {
  selectedMetric: MeetingManagerMetric;
  workspaceAverages: MeetingManagerResult;
  readAverages: ReadAverages;
}) => {
  return [
    { x: workspaceAverages[selectedMetric].value ?? 0, y: Y_TICK_VALUES[1] },
    { x: readAverages[selectedMetric].value!, y: Y_TICK_VALUES[0] },
  ];
};

const MeetingManager = () => {
  const [selected, setSelected] =
    useState<MeetingManagerMetric>("meetingScore");

  const navigate = useNavigate();
  const { setTeamFilter, setUsersFilter } = useRollupContext();

  const { isLoading, isError, refetch, workspaceAverages, readAverages } =
    useMeetingManager();

  const anyAveragesEmpty =
    workspaceAverages?.meetingScore.value == null ||
    workspaceAverages?.sentiment.value == null ||
    workspaceAverages?.engagement.value == null;

  const handleViewMeetingReports = () => {
    setTeamFilter(ALL_TEAMS);
    setUsersFilter(ALL_USERS);
    navigate(ROUTES.MEETING_REPORTS);
  };

  return (
    <WorkspaceModule>
      <ModuleHeader>
        <ModuleTitle icon={SvgBarChartSquare01}>Meeting Manager</ModuleTitle>
      </ModuleHeader>
      <WorkspaceModuleBody>
        <Stack
          direction="row"
          alignItems="center"
          sx={{ width: "100%", py: 1 }}
          justifyContent="space-between"
        >
          <Button
            variant="text"
            endIcon={<SvgChevronRight />}
            sx={{ color: "shades.primary.500" }}
            onClick={handleViewMeetingReports}
          >
            View all Meeting Reports
          </Button>
          {/* TODO: Unhide when we have zendesk */}
          {/* <Button
            variant="text"
            startIcon={<SvgHelpSquare />}
            sx={{ color: "shades.primary.300", fontSize: "12px" }}
          >
            What are indexes?
          </Button> */}
        </Stack>
        {isLoading ? (
          <LoadingState />
        ) : isError ? (
          <ErrorContent onRetry={refetch} />
        ) : anyAveragesEmpty ? (
          <EmptyContent />
        ) : (
          <>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              justifyContent="space-evenly"
              sx={{ width: "100%", mb: 1, px: 0.5 }}
            >
              <StyledButton
                className={selected === "meetingScore" ? "active" : undefined}
                onClick={() => setSelected("meetingScore")}
              >
                <Stack alignItems="center" justifyContent="center">
                  <Typography variant="statSecondary">
                    {workspaceAverages.meetingScore.value}
                  </Typography>
                  <Typography variant="bodyPrimaryMed">Read Score</Typography>
                </Stack>
              </StyledButton>
              <StyledButton
                className={selected === "sentiment" ? "active" : undefined}
                onClick={() => setSelected("sentiment")}
              >
                <Stack alignItems="center" justifyContent="center">
                  <Typography variant="statSecondary">
                    {workspaceAverages.sentiment.value}
                  </Typography>
                  <Typography variant="bodyPrimaryMed">Sentiment</Typography>
                </Stack>
              </StyledButton>
              <StyledButton
                className={selected === "engagement" ? "active" : undefined}
                onClick={() => setSelected("engagement")}
              >
                <Stack alignItems="center" justifyContent="center">
                  <Typography variant="statSecondary">
                    {workspaceAverages.engagement.value}
                  </Typography>
                  <Typography variant="bodyPrimaryMed">Engagement</Typography>
                </Stack>
              </StyledButton>
            </Stack>
            <Box sx={{ height: "130px", width: "100%" }}>
              <ParentSize>
                {(parent) => {
                  return (
                    <HorizontalBarChart
                      context="meetingManager"
                      width={parent.width}
                      height={parent.height}
                      data={getChartValues({
                        selectedMetric: selected,
                        workspaceAverages,
                        readAverages,
                      })}
                    />
                  );
                }}
              </ParentSize>
            </Box>
          </>
        )}
      </WorkspaceModuleBody>
    </WorkspaceModule>
  );
};

export default MeetingManager;
